import closeCompose from '../actions/closeCompose';
import { addPopoutV2 } from 'owa-popout-v2';
import createPostOpenTasks, { MailComposePostOpenInitProps } from './createPostOpenTasks';
import { AttachmentModel } from 'owa-attachment-model-store';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import clearDomProperties from 'owa-editor-docking-plugin/lib/actions/clearDomProperties';
import { protectionStore } from 'owa-mail-protection/lib/store/protectionStore';
import { ComposeViewState, ComposePopoutData, getStore } from 'owa-mail-compose-store';
import mailTipsCacheStore from 'owa-mail-tips/lib/store/store';
import { BohemiaEnabledScenario, isBohemiaEnabledForScenario } from 'owa-prague-integrations';
import { findTabByData } from 'owa-tab-store';
import { isFeatureEnabled } from 'owa-feature-flags';
import { logPopoutInsertLinksDatapoints } from './logPopoutInsertLinksDatapoints';

/**
 * Popout compose to a new window
 * @param viewState The ComposeViewState for the compose session to popout
 * @param postOpenInitPropsForOpenedCompose For compose already opened in main window,
 * some of the post open tasks need to be run again in popout window. Here we allow override
 * the default task to run using this parameter. If not passed, an empty object "{}" will be used
 */
export default function popoutCompose(
    viewState: ComposeViewState,
    postOpenInitPropsForOpenedCompose?: MailComposePostOpenInitProps
) {
    logPopoutInsertLinksDatapoints(viewState);
    addPopoutV2('mail', 'compose', {
        deeplinkData: () => {
            if (viewState?.docking) {
                clearDomProperties(viewState.docking);
            }

            closeCompose(viewState, 'PoppedOut');
            return createComposePopoutData(viewState, postOpenInitPropsForOpenedCompose);
        },
        projectionTabId:
            isFeatureEnabled('cmp-popout-projection') && findTabByData(viewState.composeId)?.id,
    });
}

function createComposePopoutData(
    viewState: ComposeViewState,
    postOpenInitProps: MailComposePostOpenInitProps
): ComposePopoutData {
    const attachments: AttachmentModel[] = [];

    if (viewState.attachmentWell) {
        viewState.attachmentWell.docViewAttachments.forEach(element => {
            attachments.push(getAttachment(element.attachmentId));
        });
        viewState.attachmentWell.imageViewAttachments.forEach(element => {
            attachments.push(getAttachment(element.attachmentId));
        });
        viewState.attachmentWell.inlineAttachments.forEach(element => {
            attachments.push(getAttachment(element.attachmentId));
        });
    }

    const mailtips = mailTipsCacheStore.mailTips
        ? mailTipsCacheStore.mailTips.returnAllMailTipsCacheEntries()
        : null;

    const composeViewState = {
        ...viewState,
    };

    // If this compose is already opened in main window, recreate the post open tasks
    // so that they can run again in popout window.
    if (getStore().viewStates.has(viewState.composeId)) {
        composeViewState.postOpenTasks = createPostOpenTasks(postOpenInitProps);
    }

    return {
        composeViewState,
        attachments,
        mailtips,
        protectionStore,
        includeBohemiaOnPopout: isBohemiaEnabledForScenario(BohemiaEnabledScenario.MailCompose),
    };
}
